import React, { memo } from "react";
import { SearchBarPagination } from "../SearchBox/SearchBarPagination";
import { Package, Store, Users, Truck, Globe, ShoppingCart, MapPin, Building2 } from "lucide-react";
import {
    getEntitySuggestionSearchAPI,
    ENTITY_SUGGESTION_TYPES,
} from "@/redux/Common/actions";

function PartiesInformation({
    formData,
    handleChange,
    handleCopyFields,
    inputClass = "w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white transition-all",
    textareaClass,
    setAddress,
}) {
    const baseTextarea = textareaClass || `${inputClass} min-h-[80px]`;

    // Helper to safely set address (preserves original behavior)
    // const setAddress = (fieldName, id) => {
    //     // This function should be provided by parent, but we call it as originally
    //     if (typeof window._setAddress === 'function') {
    //         window._setAddress(fieldName, id);
    //     }
    //     // If not available, do nothing – original code relied on it being defined globally or via props
    // };

    // Party configurations for left side (Shipper-related)
    const leftParties = [
        {
            key: "supplier",
            title: "Supplier / Manufacturer",
            nameField: "supplierName",
            idField: "supplierID",
            addressField: "supplierAddress",
            sameAsFlag: "sameAsShipperSupplier",
            sameAsSource: "Shipper",
            sourceIdField: "ShipperID",
            sourceAddressField: "ShipperAddress",
            icon: Package,
            color: "blue",
            entityType: ENTITY_SUGGESTION_TYPES.SUPPLIER,
        },
        {
            key: "seller",
            title: "Seller",
            nameField: "SellerName",
            idField: "SellerID",
            addressField: "SellerAddress",
            sameAsFlag: "IsSeller_SameShip",
            sameAsSource: "Shipper",
            sourceIdField: "ShipperID",
            sourceAddressField: "ShipperAddress",
            icon: Store,
            color: "emerald",
            entityType: ENTITY_SUGGESTION_TYPES.EXPORTER,
        },
        {
            key: "consolidator",
            title: "Consolidator",
            nameField: "ConsolidatorName",
            idField: "ConsolidatorID",
            addressField: "ConsolidatorAddress",
            sameAsFlag: "IsConsolidator_SameShip",
            sameAsSource: "Shipper",
            sourceIdField: "ShipperID",
            sourceAddressField: "ShipperAddress",
            icon: Users,
            color: "purple",
            entityType: ENTITY_SUGGESTION_TYPES.EXPORTER,
        },
        {
            key: "stuffer",
            title: "Stuffer",
            nameField: "StufferName",
            idField: "StufferID",
            addressField: "StufferAddress",
            sameAsFlag: "IsStuffer_SameShip",
            sameAsSource: "Shipper",
            sourceIdField: "ShipperID",
            sourceAddressField: "ShipperAddress",
            icon: Truck,
            color: "amber",
            entityType: ENTITY_SUGGESTION_TYPES.WAREHOUSE,
        },
    ];

    // Party configurations for right side (Consignee-related)
    const rightParties = [
        {
            key: "importer",
            title: "Importer",
            nameField: "ImporterName",
            idField: "ImporterID",
            addressField: "ImporterAddress",
            sameAsFlag: "IsImporter_SameConsign",
            sameAsSource: "Consignee",
            sourceIdField: "ConsigneeID",
            sourceAddressField: "ConsigneeAddress",
            icon: Globe,
            color: "cyan",
            entityType: ENTITY_SUGGESTION_TYPES.CONSIGNEE,
        },
        {
            key: "buyer",
            title: "Buyer",
            nameField: "BuyerName",
            idField: "BuyerID",
            addressField: "BuyerAddress",
            sameAsFlag: "IsBuyer_SameConsign",
            sameAsSource: "Consignee",
            sourceIdField: "ConsigneeID",
            sourceAddressField: "ConsigneeAddress",
            icon: ShoppingCart,
            color: "rose",
            entityType: ENTITY_SUGGESTION_TYPES.BCO,
        },
        {
            key: "shipTo",
            title: "Ship To",
            nameField: "ShipToName",
            idField: "ShipToID",
            addressField: "ShipToAddress",
            sameAsFlag: "IsShipTo_SameConsign",
            sameAsSource: "Consignee",
            sourceIdField: "ConsigneeID",
            sourceAddressField: "ConsigneeAddress",
            icon: MapPin,
            color: "indigo",
            entityType: ENTITY_SUGGESTION_TYPES.CONSIGNEE,
        },
    ];

    // Helper to get color classes based on color name
    const getColorClasses = (color) => {
        const colors = {
            blue: {
                bg: "from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20",
                border: "border-l-blue-500",
                iconBg: "bg-blue-100 dark:bg-blue-900/30",
                iconColor: "text-blue-600 dark:text-blue-400",
                title: "text-blue-700 dark:text-blue-400",
                checkbox: "text-blue-600 focus:ring-blue-500"
            },
            emerald: {
                bg: "from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20",
                border: "border-l-emerald-500",
                iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
                iconColor: "text-emerald-600 dark:text-emerald-400",
                title: "text-emerald-700 dark:text-emerald-400",
                checkbox: "text-emerald-600 focus:ring-emerald-500"
            },
            purple: {
                bg: "from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20",
                border: "border-l-purple-500",
                iconBg: "bg-purple-100 dark:bg-purple-900/30",
                iconColor: "text-purple-600 dark:text-purple-400",
                title: "text-purple-700 dark:text-purple-400",
                checkbox: "text-purple-600 focus:ring-purple-500"
            },
            amber: {
                bg: "from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20",
                border: "border-l-amber-500",
                iconBg: "bg-amber-100 dark:bg-amber-900/30",
                iconColor: "text-amber-600 dark:text-amber-400",
                title: "text-amber-700 dark:text-amber-400",
                checkbox: "text-amber-600 focus:ring-amber-500"
            },
            cyan: {
                bg: "from-cyan-50 to-cyan-100/50 dark:from-cyan-950/30 dark:to-cyan-900/20",
                border: "border-l-cyan-500",
                iconBg: "bg-cyan-100 dark:bg-cyan-900/30",
                iconColor: "text-cyan-600 dark:text-cyan-400",
                title: "text-cyan-700 dark:text-cyan-400",
                checkbox: "text-cyan-600 focus:ring-cyan-500"
            },
            rose: {
                bg: "from-rose-50 to-rose-100/50 dark:from-rose-950/30 dark:to-rose-900/20",
                border: "border-l-rose-500",
                iconBg: "bg-rose-100 dark:bg-rose-900/30",
                iconColor: "text-rose-600 dark:text-rose-400",
                title: "text-rose-700 dark:text-rose-400",
                checkbox: "text-rose-600 focus:ring-rose-500"
            },
            indigo: {
                bg: "from-indigo-50 to-indigo-100/50 dark:from-indigo-950/30 dark:to-indigo-900/20",
                border: "border-l-indigo-500",
                iconBg: "bg-indigo-100 dark:bg-indigo-900/30",
                iconColor: "text-indigo-600 dark:text-indigo-400",
                title: "text-indigo-700 dark:text-indigo-400",
                checkbox: "text-indigo-600 focus:ring-indigo-500"
            }
        };
        return colors[color] || colors.blue;
    };

    const renderPartyCard = (party) => {
        const colors = getColorClasses(party.color);
        const Icon = party.icon;

        return (
            <div key={party.key} className={`bg-gradient-to-br ${colors.bg} rounded-xl border-l-2 ${colors.border} border border-slate-200 dark:border-slate-700 p-4 shadow-sm hover:shadow-md transition-shadow`}>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className={`p-1.5 ${colors.iconBg} rounded-lg`}>
                            <Icon className={`w-4 h-4 ${colors.iconColor}`} />
                        </div>
                        <label className={`font-semibold ${colors.title} text-sm`}>{party.title}</label>
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-1 rounded-md shadow-sm">
                        <input
                            type="checkbox"
                            checked={formData?.[party.sameAsFlag] || false}
                            onChange={() =>
                                handleCopyFields(party.sameAsFlag, party.sameAsSource, [
                                    { name: party.nameField, valueKey: party.sameAsSource },
                                    { name: party.idField, valueKey: party.sourceIdField, clearValue: 0 },
                                    { name: party.addressField, valueKey: party.sourceAddressField }
                                ])
                            }
                            className={`rounded ${colors.checkbox} focus:ring-1`}
                        />
                        Same as {party.sameAsSource}
                    </label>
                </div>

                <SearchBarPagination
                    searchAPI={getEntitySuggestionSearchAPI(party.entityType)}
                    selectReacord={(d) => {
                        handleChange({ target: { name: party.nameField, value: d?.[1] } });
                        handleChange({ target: { name: party.idField, value: d?.[0] } });
                        setAddress(party.addressField, d?.[0]);
                    }}
                    value={formData?.[party.nameField] || ""}
                    onchange={(val) => {
                        if (!val) {
                            handleChange({ target: { name: party.nameField, value: "" } });
                            if (party.idField) handleChange({ target: { name: party.idField, value: 0 } });
                        }
                    }}
                    placeholder={`Search ${party.title.toLowerCase()}...`}
                />

                <textarea
                    name={party.addressField}
                    value={formData?.[party.addressField] || ""}
                    onChange={handleChange}
                    className={`${baseTextarea} mt-3`}
                    placeholder="Address (include phone, email if needed)"
                    rows={6}
                />
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* Header with icon */}
            <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-indigo-500" />
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Parties Information</h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT SIDE - Shipper-related parties */}
                <div className="space-y-5">
                    {leftParties.map(party => renderPartyCard(party))}
                </div>

                {/* RIGHT SIDE - Consignee-related parties */}
                <div className="space-y-5">
                    {rightParties.map(party => renderPartyCard(party))}
                </div>
            </div>
        </div>
    );
}

export default memo(PartiesInformation);